import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BrandedCard, BrandedInput, BrandedButton, BootPulse } from '../components';
import { useTenantContext } from '../hooks/useBranding';
import { TwoFactorSettings } from '../components/TwoFactorSettings';
import { LinkedAccounts } from '../components/LinkedAccounts';
import {
  kratosService,
  flowErrorMessage,
  KratosFlow,
  KratosFlowError,
  mfaPath,
} from '../services/kratosService';
import './Settings.css';

// Mirrors the Kratos identity schema (kratos/config/identity.schema.json),
// which sets additionalProperties:false — any trait not declared there makes
// the whole settings flow fail validation.
interface UserProfile {
  email: string;
  first: string;
  last: string;
  phone: string;
}

/** A settings flow carries the identity it edits. */
type SettingsFlow = KratosFlow & {
  identity?: { traits?: { email?: string; name?: { first?: string; last?: string }; phone?: string } };
};

/** What a two-factor or linked-account submission did, in words. */
function methodOutcome(body: Record<string, unknown>): string {
  if (body.totp_code) return 'Two-factor authentication is on. Signing in now also asks for a code from your app.';
  if (body.totp_unlink) return 'Authenticator app removed.';
  if (body.lookup_secret_regenerate) return 'New backup codes generated. Save them, then confirm.';
  if (body.lookup_secret_confirm) return 'Backup codes saved.';
  if (body.lookup_secret_disable) return 'Backup codes turned off.';
  if (body.unlink) return 'Account unlinked. You can no longer sign in with it.';
  return '';
}

export const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { context, loading: contextLoading } = useTenantContext();

  const [profile, setProfile] = useState<UserProfile>({
    email: '',
    first: '',
    last: '',
    phone: '',
  });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [kratosAvailable, setKratosAvailable] = useState(false);
  const [flowId, setFlowId] = useState('');
  // The whole settings flow: the two-factor section renders from its nodes.
  const [settingsFlow, setSettingsFlow] = useState<KratosFlow | null>(null);

  // Check authentication and load profile
  useEffect(() => {
    const checkAuth = async () => {
      // A Kratos session cookie is the primary credential; the sessionStorage
      // token exists only on the fallback-auth path.
      const { session, secondFactorRequired } = await kratosService.sessionState();
      if (secondFactorRequired) {
        navigate(mfaPath('/settings'));
        return;
      }
      if (session) return;
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        navigate('/login?return_to=/settings');
        return;
      }
    };

    const checkKratos = async () => {
      if (context?.enabled_components.includes('kratos')) {
        const available = await kratosService.isAvailable();
        setKratosAvailable(available);

        if (available) {
          try {
            // Back from linking a social account, Kratos sends ?flow= with
            // the outcome on it: show that flow and its message.
            const returned = searchParams.get('flow');
            const adopted = returned
              ? await kratosService.getFlow('settings', returned).catch(() => null)
              : null;
            let flow: SettingsFlow | null = adopted;
            if (!flow) {
              // Initialize settings flow (Accept: json — see kratosService)
              const response = await fetch(
                `${import.meta.env.VITE_KRATOS_URL || 'http://localhost:4433'}/self-service/settings/browser`,
                { credentials: 'include', headers: { Accept: 'application/json' } }
              );
              const started = await response.json();
              // Settings demand the second factor once the account has one.
              if (response.status === 403 && started?.error?.id === 'session_aal2_required') {
                navigate(mfaPath('/settings'));
                return;
              }
              flow = started;
            }
            if (!flow) return;
            setFlowId(flow.id);
            setSettingsFlow(flow);
            const outcome = adopted?.ui?.messages?.[0];
            if (outcome?.type === 'error') setError(outcome.text);
            else if (outcome) setSuccess(outcome.text);

            // Load current profile from flow (schema shape: email, name.*, phone)
            if (flow.identity?.traits) {
              const traits = flow.identity.traits;
              setProfile({
                email: traits.email || '',
                first: traits.name?.first || '',
                last: traits.name?.last || '',
                phone: traits.phone || '',
              });
            }
          } catch (err) {
            console.error('Failed to initialize Kratos settings flow:', err);
            setKratosAvailable(false);
          }
        }
      }
    };

    checkAuth();
    if (context) {
      checkKratos();
    }
  }, [context, navigate, searchParams]);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (kratosAvailable && flowId) {
        // Use Kratos settings flow (browser flows require the csrf_token)
        const csrf_token = await kratosService.getCsrfToken('settings', flowId);
        const response = await fetch(
          `${import.meta.env.VITE_KRATOS_URL || 'http://localhost:4433'}/self-service/settings?flow=${flowId}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              method: 'profile',
              csrf_token,
              // Exactly the traits the identity schema declares; `phone` is
              // omitted when blank so it doesn't fail the format check.
              traits: {
                email: profile.email,
                name: { first: profile.first, last: profile.last },
                ...(profile.phone ? { phone: profile.phone } : {}),
              },
            }),
          }
        );

        const payload = await response.json().catch(() => undefined);
        if (!response.ok) {
          throw new Error(flowErrorMessage(payload, 'Profile update failed'));
        }

        // A submitted settings flow is spent — adopt the refreshed one Kratos
        // returns so a second edit in the same visit doesn't fail.
        if (payload?.id) {
          setFlowId(payload.id);
          setSettingsFlow(payload);
        }
        setSuccess('Profile updated successfully');
      } else {
        // Use fallback profile update
        if (!context) {
          throw new Error('Tenant context not loaded');
        }

        const token = sessionStorage.getItem('access_token');
        const response = await fetch(
          `${import.meta.env.VITE_CONTROL_PLANE_API || 'http://localhost:3000'}/api/auth/profile`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              'X-Tenant-ID': context.tenant_id,
            },
            body: JSON.stringify(profile),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Profile update failed');
        }

        setSuccess('Profile updated successfully');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Profile update failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      if (kratosAvailable && flowId) {
        // Use Kratos password change (browser flows require the csrf_token)
        const csrf_token = await kratosService.getCsrfToken('settings', flowId);
        const response = await fetch(
          `${import.meta.env.VITE_KRATOS_URL || 'http://localhost:4433'}/self-service/settings?flow=${flowId}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              method: 'password',
              password: newPassword,
              csrf_token,
            }),
          }
        );

        const payload = await response.json().catch(() => undefined);
        if (!response.ok) {
          // Changing credentials needs a PRIVILEGED session (authenticated
          // within privileged_session_max_age). Kratos refuses otherwise —
          // send the visitor through a refresh login and come straight back
          // instead of showing a bare "forbidden".
          const errorId = payload?.error?.id;
          if (errorId === 'session_refresh_required' || errorId === 'session_aal2_required') {
            const redirectTo =
              payload?.redirect_browser_to ||
              `/login?refresh=true&return_to=${encodeURIComponent('/settings')}`;
            window.location.href = redirectTo;
            return;
          }
          throw new Error(flowErrorMessage(payload, 'Password change failed'));
        }

        if (payload?.id) {
          setFlowId(payload.id);
          setSettingsFlow(payload);
        }
        setSuccess('Password changed successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        // Use fallback password change
        if (!context) {
          throw new Error('Tenant context not loaded');
        }

        const token = sessionStorage.getItem('access_token');
        const response = await fetch(
          `${import.meta.env.VITE_CONTROL_PLANE_API || 'http://localhost:3000'}/api/auth/password`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              'X-Tenant-ID': context.tenant_id,
            },
            body: JSON.stringify({
              current_password: currentPassword,
              new_password: newPassword,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Password change failed');
        }

        setSuccess('Password changed successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Password change failed');
    } finally {
      setLoading(false);
    }
  };

  // Two-factor methods (totp, lookup_secret) and linked accounts (oidc)
  // submit through the same flow.
  const handleFlowMethod = async (body: Record<string, unknown>) => {
    if (!flowId) return;
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const updated = await kratosService.submitSettings(flowId, body);
      if (updated?.id) {
        setFlowId(updated.id);
        setSettingsFlow(updated);
      }
      setSuccess(methodOutcome(body));
    } catch (err) {
      if (err instanceof KratosFlowError) {
        const payload = err.kratosError;
        const errorId = payload?.error?.id;
        // Linking a social account: Kratos sends the browser to the provider
        // to prove the account is this person's, then back here (?flow=).
        if (errorId === 'browser_location_change_required' && typeof payload?.redirect_browser_to === 'string') {
          window.location.assign(payload.redirect_browser_to);
          return;
        }
        // Changing a credential needs a recent sign-in, and (once the account
        // has one) the second factor.
        if (errorId === 'session_refresh_required') {
          window.location.href =
            (payload?.redirect_browser_to as string | undefined) ||
            `/login?refresh=true&return_to=${encodeURIComponent('/settings')}`;
          return;
        }
        if (errorId === 'session_aal2_required') {
          navigate(mfaPath('/settings'));
          return;
        }
        // A rejected code comes back as the flow with the message on its
        // node: adopt it so the section shows why.
        if (payload?.id && payload.ui) {
          setFlowId(String(payload.id));
          setSettingsFlow(payload as unknown as KratosFlow);
        }
      }
      setError(err instanceof Error ? err.message : 'Saving your settings failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('access_token');
    navigate('/login');
  };

  if (contextLoading) {
    return (
      <BrandedCard>
        <BootPulse label="Loading settings" />
      </BrandedCard>
    );
  }

  return (
    <div className="settings-container">
      <BrandedCard title="Account Settings">
        {error && (
          <div className="settings-error" role="alert">
            {error}
          </div>
        )}
        {success && (
          <div className="settings-success" role="status">
            {success}
          </div>
        )}

        <form onSubmit={handleProfileUpdate} className="settings-form">
          <h3 className="settings-section-title">Profile Information</h3>

          <BrandedInput
            type="email"
            name="email"
            label="Email"
            value={profile.email}
            onChange={(e) => setProfile({ ...profile, email: e.target.value })}
            required
            autoComplete="email"
          />

          <BrandedInput
            type="text"
            name="first_name"
            label="First Name"
            value={profile.first}
            onChange={(e) => setProfile({ ...profile, first: e.target.value })}
            autoComplete="given-name"
          />

          <BrandedInput
            type="text"
            name="last_name"
            label="Last Name"
            value={profile.last}
            onChange={(e) => setProfile({ ...profile, last: e.target.value })}
            autoComplete="family-name"
          />

          <BrandedInput
            type="tel"
            name="phone"
            label="Phone"
            value={profile.phone}
            onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
            autoComplete="tel"
          />

          <BrandedButton
            type="submit"
            variant="primary"
            fullWidth
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Profile'}
          </BrandedButton>
        </form>

        <form onSubmit={handlePasswordChange} className="settings-form">
          <h3 className="settings-section-title">Change Password</h3>

          {!kratosAvailable && (
            <BrandedInput
              type="password"
              name="current_password"
              label="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          )}

          <BrandedInput
            type="password"
            name="new_password"
            label="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Min. 8 characters"
            required
            autoComplete="new-password"
          />

          <BrandedInput
            type="password"
            name="confirm_password"
            label="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            autoComplete="new-password"
          />

          <BrandedButton
            type="submit"
            variant="secondary"
            fullWidth
            disabled={loading}
          >
            {loading ? 'Changing...' : 'Change Password'}
          </BrandedButton>
        </form>

        {settingsFlow && (
          <TwoFactorSettings flow={settingsFlow} onSubmit={handleFlowMethod} busy={loading} />
        )}

        {settingsFlow && (
          <LinkedAccounts flow={settingsFlow} onSubmit={handleFlowMethod} busy={loading} />
        )}

        <div className="settings-actions">
          <BrandedButton
            onClick={handleLogout}
            variant="outline"
            fullWidth
          >
            Sign Out
          </BrandedButton>
        </div>

        {!kratosAvailable && context?.enabled_components.includes('kratos') && (
          <div className="settings-info">
            Using fallback settings (Kratos unavailable)
          </div>
        )}
      </BrandedCard>
    </div>
  );
};
